/**
 * @fileoverview Hata Mesajlari ve Hata Yonetimi
 *
 * Bu modul widget genelinde tutarli kullanici geri bildirimi icin
 * merkezi hata mesajlarini ve hata formatlama fonksiyonlarini saglar.
 *
 * Tum hata mesajlari Turkce olarak tanimlanmistir ve kullanici arayuzunde
 * dogrudan gosterilebilir.
 *
 * @module utils/errors
 *
 * @example
 * // Hata mesaji kullanimi:
 * import { ERROR_MESSAGES, formatError } from './errors';
 *
 * try {
 *   await searchListings(params);
 * } catch (error) {
 *   setError(formatError(error, 'SEARCH_FAILED'));
 * }
 */

/**
 * Merkezi hata mesajlari sozlugu.
 * Tum mesajlar Turkce olarak tanimlanmistir.
 *
 * @constant {Object}
 *
 * @property {string} API_UNAVAILABLE - API erisim hatasi (sandbox/mock ortam)
 * @property {string} SEARCH_FAILED - Genel arama hatasi
 * @property {string} DETAIL_FAILED - Ilan detay cekme hatasi
 * @property {string} LOAD_MORE_FAILED - Sayfalama/daha fazla yukleme hatasi
 * @property {string} REGION_PRICE_FAILED - Bolge fiyat bilgisi hatasi
 * @property {string} MAP_SEARCH_FAILED - Harita tabanli arama hatasi
 *
 * @property {string} PATH_REQUIRED - Arama yolu eksik (kisa mesaj)
 * @property {string} PATH_REQUIRED_VERBOSE - Arama yolu eksik (detayli mesaj)
 * @property {string} BOUNDS_REQUIRED - Harita sinirlari eksik
 * @property {string} INVALID_PATH - Gecersiz arama yolu formati
 *
 * @property {string} UNKNOWN_ERROR - Bilinmeyen/beklenmeyen hata
 *
 * @example
 * // Dogrudan mesaj kullanimi:
 * if (!path) {
 *   setError(ERROR_MESSAGES.PATH_REQUIRED_VERBOSE);
 *   return;
 * }
 *
 * // API hatasi kontrolu:
 * if (!window.openai?.callTool) {
 *   console.warn(ERROR_MESSAGES.API_UNAVAILABLE);
 * }
 */
export const ERROR_MESSAGES = {
  // ==================== API Errors ====================
  // API Hatalari - Ag ve sunucu kaynaklarindan gelen hatalar

  /** API sandbox/mock ortamda kullanilamadiginda */
  API_UNAVAILABLE: "Bu ortamda islem kullanilamiyor.",

  /** callTool API mevcut olmadiginda (sandbox ortam) */
  CALL_TOOL_UNAVAILABLE: "Bu ortamda callTool() kullanilamiyor.",

  /** sendFollowUpMessage API mevcut olmadiginda */
  SEND_MESSAGE_UNAVAILABLE: "Bu ortamda sendFollowUpMessage() kullanilamiyor.",

  /** Genel arama istegi basarisiz oldugunda */
  SEARCH_FAILED: "Arama yapilamadi. Lutfen tekrar deneyin.",

  /** Ilan detay istegi basarisiz oldugunda */
  DETAIL_FAILED: "Ilan detayi alinamadi.",

  /** Sayfalama/daha fazla yukleme basarisiz oldugunda */
  LOAD_MORE_FAILED: "Daha fazla yukleme basarisiz.",

  /** Bolge fiyat bilgisi alinamadiginda */
  REGION_PRICE_FAILED: "Bolge fiyat bilgisi alinamadi.",

  /** Harita viewport araması basarisiz oldugunda */
  MAP_SEARCH_FAILED: "Harita aramasi basarisiz.",

  // ==================== Validation Errors ====================
  // Dogrulama Hatalari - Kullanici girdisi veya state dogrulama hatalari

  /** Arama yolu (path) parametresi eksik oldugunda (kisa versiyon) */
  PATH_REQUIRED: "Arama yolu (path) bulunamadi.",

  /** Arama yolu (path) parametresi eksik oldugunda (detayli versiyon) */
  PATH_REQUIRED_VERBOSE: "Arama yolu (path) bulunamadi. Yeni bir arama icin sohbetten istek verin.",

  /** Harita sinirlari (bounds) parametresi eksik oldugunda */
  BOUNDS_REQUIRED: "Harita sinirlari bulunamadi.",

  /** Arama yolu formati gecersiz oldugunda */
  INVALID_PATH: "Gecersiz arama yolu.",

  // ==================== Generic ====================
  // Genel Hatalar - Siniflandirilamamis hatalar icin

  /** Beklenmeyen/bilinmeyen hata durumlarinda */
  UNKNOWN_ERROR: "Beklenmeyen bir hata olustu.",
};

/**
 * Hata nesnesini kullanici dostu mesaja donusturur.
 *
 * Bu fonksiyon farkli hata turlerini ele alir:
 * - Error instance'lari: error.message kullanilir
 * - String hatalar: dogrudan string kullanilir
 * - Diger tipler: fallbackKey ile ERROR_MESSAGES'tan mesaj alinir
 *
 * @param {unknown} error - Formatlanacak hata (Error, string veya any)
 * @param {string} [fallbackKey="UNKNOWN_ERROR"] - Error instance degilse kullanilacak ERROR_MESSAGES anahtari
 * @returns {string} Kullanici dostu hata mesaji
 *
 * @example
 * // Error instance ile:
 * try {
 *   throw new Error("Network timeout");
 * } catch (error) {
 *   const message = formatError(error);
 *   // Sonuc: "Network timeout"
 * }
 *
 * @example
 * // Fallback key ile:
 * const message = formatError(null, "SEARCH_FAILED");
 * // Sonuc: "Arama yapilamadi. Lutfen tekrar deneyin."
 *
 * @example
 * // Bilinmeyen hata tipi:
 * const message = formatError(undefined);
 * // Sonuc: "Beklenmeyen bir hata olustu."
 */
export function formatError(error, fallbackKey = "UNKNOWN_ERROR") {
  // Error instance ise message property'sini kullan
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback key ile ERROR_MESSAGES'tan mesaj al
  // Gecersiz key ise UNKNOWN_ERROR kullan
  return ERROR_MESSAGES[fallbackKey] || ERROR_MESSAGES.UNKNOWN_ERROR;
}
